import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Send,
  Email,
  Phone,
  Business,
  Person,
  Message as MessageIcon,
  LocationOn,
  Schedule,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Language,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients } from "../../../utils/theme";
import AnimatedSection from "../AnimatedSection";
import { useSubmitLeadMutation } from "../../../features/leads/leadsApiSlice";

/**
 * Form Field Component Props
 */
interface FormFieldProps {
  label: string;
  field: string;
  value: string;
  onInputChange: (field: string, value: string) => void;
  error?: string;
  disabled: boolean;
  icon?: React.ReactElement;
  type?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}

/**
 * Reusable Form Field Component
 */
const FormField: React.FC<FormFieldProps> = ({
  label,
  field,
  value,
  onInputChange,
  error,
  disabled,
  icon,
  type = "text",
  multiline = false,
  rows,
  placeholder,
}) => {
  return (
    <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
      <TextField
        fullWidth
        label={label}
        variant="outlined"
        required
        type={type}
        multiline={multiline}
        rows={rows}
        value={value}
        onChange={(e) => onInputChange(field, e.target.value)}
        error={!!error}
        helperText={error}
        disabled={disabled}
        placeholder={placeholder}
        InputProps={
          icon
            ? {
                startAdornment: React.cloneElement(icon, {
                  sx: { mr: 1, color: "action.active" },
                }),
              }
            : undefined
        }
        sx={{
          "& .MuiOutlinedInput-root": {
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(26, 35, 126, 0.15)",
            },
            "&.Mui-focused": {
              boxShadow: "0 4px 12px rgba(26, 35, 126, 0.25)",
            },
          },
        }}
      />
    </motion.div>
  );
};

/**
 * Contact Info Item Component Props
 */
interface ContactInfoItemProps {
  icon: React.ReactElement;
  title: string;
  value: string;
}

/**
 * Reusable Contact Info Item Component
 */
const ContactInfoItem: React.FC<ContactInfoItemProps> = ({
  icon,
  title,
  value,
}) => {
  return (
    <Box sx={{ textAlign: "center" }}>
      {React.cloneElement(icon, {
        sx: { fontSize: 40, color: "primary.main", mb: 1 },
      })}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {value}
      </Typography>
    </Box>
  );
};

/**
 * Social Media Link Component Props
 */
interface SocialLinkProps {
  icon: React.ReactElement;
  href: string;
  label: string;
}

/**
 * Social Media Link Component
 */
const SocialLink: React.FC<SocialLinkProps> = ({ icon, href, label }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      whileHover={{ scale: 1.15, y: -3 }}
      whileTap={{ scale: 0.95 }}
      style={{
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: gradients.navyLux,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          transition: "all 0.3s ease",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          "&:hover": {
            boxShadow: "0 6px 20px rgba(26, 35, 126, 0.4)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 24 } })}
      </Box>
    </motion.a>
  );
};

/**
 * Contact Form Section Component
 *
 * Self-contained component with its own state, validation, and submission logic.
 * No prop drilling required - everything is managed internally.
 */
const ContactFormSection: React.FC = () => {
  // RTK Query mutation for lead submission
  const [submitLead, { isLoading: isSubmitting }] = useSubmitLeadMutation();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success",
  );

  // Validate single field (real-time validation)
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 2)
          return "Name should be at least 2 characters";
        if (value.trim().length > 50)
          return "Name should not exceed 50 characters";
        return "";

      case "company":
        if (!value.trim()) return "Company name is required";
        if (value.trim().length < 2)
          return "Company name should be at least 2 characters";
        if (value.trim().length > 100)
          return "Company name should not exceed 100 characters";
        return "";

      case "email":
        if (!value.trim()) return "Email is required";
        if (value.length > 100) return "Email should not exceed 100 characters";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email address";
        return "";

      case "phone":
        if (!value.trim()) return "Phone number is required";
        const cleanedPhone = value.replace(/[\s()-]/g, "");
        if (!/^[0-9]{10,15}$/.test(cleanedPhone))
          return "Phone should be 10-15 digits";
        return "";

      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10)
          return "Message should be at least 10 characters";
        if (value.trim().length > 500)
          return "Message should not exceed 500 characters";
        return "";

      default:
        return "";
    }
  };

  // Form validation
  const validateForm = () => {
    const errors: Record<string, string> = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(
        field,
        formData[field as keyof typeof formData],
      );
      if (error) errors[field] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input changes with real-time validation
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    const error = validateField(field, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setToastMessage("Please fix the errors in the form");
      setToastSeverity("error");
      setToastOpen(true);
      return;
    }

    try {
      const result = await submitLead(formData).unwrap();
      setToastMessage(
        result.message || "Thank you! We'll get back to you within 24 hours.",
      );
      setToastSeverity("success");
      setToastOpen(true);

      // Reset form
      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
      });
      setFormErrors({});
    } catch (error: any) {
      console.error("Form submission error:", error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again or contact us directly.";
      setToastMessage(errorMessage);
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 4, sm: 5, md: 6, lg: 8 },
        background: "linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="md">
        <AnimatedSection>
          <Typography
            variant="h2"
            sx={{
              fontSize: {
                xs: "2rem",
                sm: "2.25rem",
                md: "2.5rem",
                lg: "2.75rem",
              },
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 6,
              textAlign: "center",
              color: "text.secondary",
              fontWeight: 400,
            }}
          >
            Ready to revolutionize your mall experience? Let's talk!
          </Typography>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <Card elevation={3}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      label="Your Name"
                      field="name"
                      value={formData.name}
                      onInputChange={handleInputChange}
                      error={formErrors.name}
                      disabled={isSubmitting}
                      icon={<Person />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      label="Company Name"
                      field="company"
                      value={formData.company}
                      onInputChange={handleInputChange}
                      error={formErrors.company}
                      disabled={isSubmitting}
                      icon={<Business />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      label="Email Address"
                      field="email"
                      type="email"
                      value={formData.email}
                      onInputChange={handleInputChange}
                      error={formErrors.email}
                      disabled={isSubmitting}
                      icon={<Email />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      label="Phone Number"
                      field="phone"
                      type="tel"
                      value={formData.phone}
                      onInputChange={handleInputChange}
                      error={formErrors.phone}
                      disabled={isSubmitting}
                      icon={<Phone />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      label="Tell us about your mall and requirements"
                      field="message"
                      value={formData.message}
                      onInputChange={handleInputChange}
                      error={formErrors.message}
                      disabled={isSubmitting}
                      multiline
                      rows={4}
                      placeholder="Mall size, footfall, current challenges, features you're interested in (stores, offers, events, parking, analytics)..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <motion.div
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        type="submit"
                        disabled={isSubmitting}
                        endIcon={<Send />}
                        sx={{
                          py: 1.5,
                          fontSize: "1.125rem",
                          fontWeight: 600,
                          textTransform: "none",
                          background: gradients.navyLux,
                          color: "white",
                          boxShadow: "0 4px 16px rgba(26, 35, 126, 0.4)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #0d1b5e 0%, #1e2875 100%)",
                            boxShadow: "0 6px 24px rgba(26, 35, 126, 0.6)",
                          },
                          "&:disabled": {
                            background: "rgba(26, 35, 126, 0.6)",
                            color: "rgba(255, 255, 255, 0.7)",
                          },
                        }}
                      >
                        {isSubmitting ? "Sending..." : "Send Message"}
                      </Button>
                    </motion.div>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </AnimatedSection>

        {/* Contact Info */}
        <AnimatedSection delay={0.4}>
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} sm={4} md={4}>
              <ContactInfoItem
                icon={<Email />}
                title="Email Us"
                value="support@mallinhand.com"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <ContactInfoItem
                icon={<Phone />}
                title="Call Us"
                value="+91 98765-43210"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <ContactInfoItem
                icon={<MessageIcon />}
                title="WhatsApp"
                value="+91 98765-43210"
              />
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* Address and Business Hours */}
        <AnimatedSection delay={0.5}>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <LocationOn
                  sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Our Office
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  MallInHand.
                  <br />
                  Pune, Maharashtra 411036
                  <br />
                  India
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Card
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Schedule sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Business Hours
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  Monday - Saturday: 9:00 AM - 6:00 PM
                  <br />
                  Sunday: Closed
                  <br />
                  <Box
                    component="span"
                    sx={{
                      color: "success.main",
                      fontWeight: 600,
                      mt: 1,
                      display: "inline-block",
                    }}
                  >
                    24/7 Support Available
                  </Box>
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* Social Media Links */}
        <AnimatedSection delay={0.6}>
          <Box sx={{ mt: 6, textAlign: "center" }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
              Connect With Us
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <SocialLink
                icon={<Facebook />}
                href="https://facebook.com/mallinhand"
                label="Facebook"
              />
              <SocialLink
                icon={<Twitter />}
                href="https://twitter.com/mallinhand"
                label="Twitter"
              />
              <SocialLink
                icon={<Instagram />}
                href="https://instagram.com/mallinhand"
                label="Instagram"
              />
              <SocialLink
                icon={<LinkedIn />}
                href="https://linkedin.com/company/mallinhand"
                label="LinkedIn"
              />
              <SocialLink
                icon={<Language />}
                href="https://mallinhand.com"
                label="Website"
              />
            </Box>
          </Box>
        </AnimatedSection>

        {/* Toast Notifications */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={6000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleToastClose}
            severity={toastSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactFormSection;
